import { runtimeConfig } from "@/runtime-config";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { SettingsApiTypes } from "@stellar/settings-api-client";

export type ThemeState = {
  /** The `theme_mode` user setting, undefined if the user never set it */
  themeMode?: SettingsApiTypes.SupportedThemeModes;
  /** True once the initial `theme_mode` request has settled, so local changes can't be overwritten by it */
  isThemeModeLoaded: boolean;
};

type OnlyThemeState = { theme: ThemeState };

const initialState: Readonly<ThemeState> = Object.freeze({
  isThemeModeLoaded: false,
});

const themeSlice = createSlice({
  initialState,
  name: "theme",
  reducers: {
    /**
     * @param state of this slice
     * @param action the new theme mode, or undefined to reset it
     */
    setThemeMode(
      state,
      action: PayloadAction<SettingsApiTypes.SupportedThemeModes | undefined>,
    ) {
      state.themeMode = action.payload;
    },
    /**
     * @param state of this slice
     * @param action the loaded theme mode, or undefined if the user never set it or loading failed
     */
    themeModeLoaded(
      state,
      action: PayloadAction<SettingsApiTypes.SupportedThemeModes | undefined>,
    ) {
      state.themeMode = action.payload;
      state.isThemeModeLoaded = true;
    },
  },
});

export const { setThemeMode, themeModeLoaded } = themeSlice.actions;

export const themeReducer = themeSlice.reducer;

/** @returns false on prod deployments, which always use the light theme while dark mode is work in progress */
export function isThemeModeSettingEnabled(): boolean {
  return !runtimeConfig.deployment.environment.includes("prod");
}

/**
 * @param state the root application state
 * @returns the theme mode to apply: light unless the user explicitly chose dark or system
 */
export function selectThemeMode({
  theme,
}: OnlyThemeState): SettingsApiTypes.SupportedThemeModes {
  if (!isThemeModeSettingEnabled()) return "light";
  return theme.themeMode ?? "light";
}

/**
 * @param state the root application state
 * @returns true once the `theme_mode` user setting has been loaded
 */
export function selectIsThemeModeLoaded({ theme }: OnlyThemeState): boolean {
  return theme.isThemeModeLoaded;
}
